import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApplicationPackage } from 'src/app/enums/application-package.enum';
import { Service } from 'src/app/enums/service.enum';

import { Banner } from 'src/app/model/cms-vod-core/banner-model/banner-model';
import { BannerPosterDefinition } from 'src/app/model/cms-vod-core/banner-model/banner-poster-definition';

@Injectable({ providedIn: 'root' })
export class BannerService {
  private readonly bannerApiUrl =
    environment.endPoint + ApplicationPackage.CMS_VOD + Service.BANNER;

  private readonly bannerPosterDefinitionApiUrl =
    environment.endPoint + ApplicationPackage.CMS_VOD + Service.BANNER_POSTER_DEFINITION;

  private targetsBase = '/api/banner-targets';

  constructor(private http: HttpClient) {}

  // =========================
  // LIST / SEARCH
  // =========================
  search(options: any): Observable<any> {
  let params = new HttpParams();

  if (options) {
    if (options.name) params = params.set('name', String(options.name));
    if (options.targetType) params = params.set('targetType', String(options.targetType));
    if (options.targetName) params = params.set('targetName', String(options.targetName));

    if (options.status !== undefined && options.status !== null) {
      params = params.set('status', String(options.status));
    }
    if (options.isChild !== undefined && options.isChild !== null) {
      params = params.set('isChild', String(options.isChild));
    }

    // ✅ DATE RANGE (epoch millis) -> controller paramları: startFrom, endTo
    if (options.startFrom !== undefined && options.startFrom !== null) {
      params = params.set('startFrom', String(options.startFrom));
    }
    if (options.endTo !== undefined && options.endTo !== null) {
      params = params.set('endTo', String(options.endTo));
    }

    const page = (options.page !== undefined && options.page !== null) ? options.page : 0;
    const size = (options.size !== undefined && options.size !== null) ? options.size : 10;
    const sort = (options.sort !== undefined && options.sort !== null) ? options.sort : 'sequence,asc';

    params = params.set('page', String(page));
    params = params.set('size', String(size));
    params = params.set('sort', String(sort));
  }

  return this.http.get(this.bannerApiUrl, { params });
}

  getById(id: number): Observable<any> {
    return this.http.get(this.bannerApiUrl + '/' + id);
  }

  // =========================
  // CREATE / UPDATE (JSON) ✅
  // =========================
  create(req: any): Observable<number> {
    return this.http.post<number>(this.bannerApiUrl, req);
  }

  createWithPosters(formData: FormData): Observable<number> {
    return this.http.post<number>(this.bannerApiUrl, formData);
  }

  update(id: number, req: any): Observable<any> {
    return this.http.put<any>(this.bannerApiUrl + '/' + id, req);
  }

  updateWithPosters(id: number, formData: FormData): Observable<any> {
    return this.http.post<any>(this.bannerApiUrl + '/' + id, formData);
  }

  delete(id: number): Observable<any> {
    return this.http.delete(this.bannerApiUrl + '/' + id);
  }

  // =========================
  // STATUS / IS-CHILD / ORDER
  // =========================
  updateStatus(id: number, status: boolean): Observable<any> {
    const params = new HttpParams().set('status', String(status));
    return this.http.put(this.bannerApiUrl + '/' + id + '/status', null, { params });
  }

  updateIsChild(id: number, isChild: boolean): Observable<any> {
    return this.http.put(this.bannerApiUrl + '/' + id + '/is-child', null, {
      params: new HttpParams().set('isChild', String(isChild)),
    });
  }

  updateOrder(orderedIds: number[]): Observable<any> {
    return this.http.put(this.bannerApiUrl + '/order', orderedIds);
  }

  previewAutoSort(rule: string): Observable<number[]> {
    const params = new HttpParams().set('rule', rule);
    return this.http.get<number[]>(this.bannerApiUrl + '/auto-sort-preview', { params });
  }

  previewHybridSort(rule: string): Observable<number[]> {
    const params = new HttpParams().set('rule', rule);
    return this.http.get<number[]>(this.bannerApiUrl + '/hybrid-sort-preview', { params });
  }

  pinBanner(id: number, position: number, rule: string): Observable<any> {
    const params = new HttpParams().set('position', String(position)).set('rule', rule);
    return this.http.put(this.bannerApiUrl + '/' + id + '/pin', null, { params });
  }

  unpinBanner(id: number): Observable<any> {
    return this.http.delete(this.bannerApiUrl + '/' + id + '/pin');
  }

  // =========================
  // TARGET SEARCH
  // =========================
  searchTargets(targetType: string, query: string): Observable<any> {
    let params = new HttpParams();
    if (query) params = params.set('query', query);
    return this.http.get(this.targetsBase + '/' + targetType + '/search', { params });
  }

  // =========================
  // POSTER DEFS
  // =========================
  getAllBannerPosterDefinition(): Observable<BannerPosterDefinition[]> {
    return this.http.get<BannerPosterDefinition[]>(this.bannerPosterDefinitionApiUrl);
  }

  // =========================
  // POSTERS (MULTIPART) ✅
  // POST /banners/{id}/upload-posters
  // =========================
  uploadPosters(bannerId: number, pendingFilesMap: any): Observable<any> {
    const fd = new FormData();

    const defIds: number[] = pendingFilesMap
      ? Object.keys(pendingFilesMap)
          .map(k => parseInt(k, 10))
          .filter(x => !isNaN(x))
      : [];

    for (let i = 0; i < defIds.length; i++) {
      const defId = defIds[i];
      const file: File = pendingFilesMap[defId];
      if (!file) continue;

      fd.append('posterFiles', file, file.name);
      fd.append('posterDefinitionIds', String(defId));
    }

    return this.http.post<any>(this.bannerApiUrl + '/' + bannerId + '/upload-posters', fd);
  }

  // =========================
  // POSTERS DELETE ✅
  // DELETE /banners/{id}/posters  (body: [1,2,3])
  // =========================
  deletePosters(bannerId: number, posterDefinitionIds: number[]): Observable<any> {
    return this.http.request('delete', this.bannerApiUrl + '/' + bannerId + '/posters', {
      body: posterDefinitionIds || [],
    });
  }
}
